import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mailbox, Loader2, FileText, Megaphone } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export function InvestorDigestCard() {
  const { user, reload } = useAuth();
  const [enabled, setEnabled] = useState(!user?.investor_digest_opt_out);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => { setEnabled(!user?.investor_digest_opt_out); }, [user?.investor_digest_opt_out]);
  useEffect(() => {
    api.get("/portal/investor-digest/preview")
      .then(({ data }) => setPreview(data))
      .catch(() => setPreview({ items: [], total_updates: 0, total_docs: 0, active_count: 0 }));
  }, []);

  const toggle = async (v) => {
    setSaving(true);
    setEnabled(v);
    try {
      await api.patch("/portal/investor-digest/pref", { enabled: v });
      toast.success(v ? "Weekly investor digest on" : "Weekly investor digest off");
      reload();
    } catch (e) {
      setEnabled(!v);
      toast.error(e?.response?.data?.detail || "Could not save preference");
    } finally {
      setSaving(false);
    }
  };

  const companies = preview?.items?.length || 0;
  return (
    <Card className="rounded-xl border bg-card p-6" data-testid="investor-digest-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Mailbox className="h-5 w-5 text-primary" />
          <div>
            <h2 className="h-display text-lg font-semibold text-foreground">Weekly investor digest</h2>
            <p className="text-xs text-muted-foreground">One email a week rolling up new updates and freshly shared documents across every company you back.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          <Switch checked={enabled} onCheckedChange={toggle} disabled={saving} aria-label="Weekly investor digest" data-testid="investor-digest-toggle" />
        </div>
      </div>
      <div className="mt-3 text-xs text-muted-foreground" data-testid="investor-digest-summary">
        {preview === null ? "Checking your companies…" : companies === 0
          ? "You're not linked to any company yet. Once a founder adds you as an investor, your weekly roundup starts here."
          : (
            <span className="flex flex-wrap items-center gap-x-1 gap-y-1">
              Across <Badge variant="secondary" className="font-normal">{companies}</Badge>{companies === 1 ? "company" : "companies"} this week:
              <span className="inline-flex items-center gap-1"><Megaphone className="h-3.5 w-3.5" /> {preview.total_updates || 0} updates</span>
              ·
              <span className="inline-flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> {preview.total_docs || 0} documents</span>
            </span>
          )}
      </div>
    </Card>
  );
}
